import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import type { PropsWithChildren, RefObject } from 'react'
import { useRef, Fragment, useState } from 'react'
import { createPortal } from 'react-dom'
import { classNames } from '~/utils/client/helpers'
import { useIsHydrated } from '~/utils/useIsHydrated'
import { useSelectDropdownPosition } from './useSelectDropdownPosition'

type SelectOption = {
  value: string
  label: string
}

export type SelectOptionItem = SelectOption & PropsWithChildren

export type SelectProps = {
  label?: string
  name: string
  defaultValue?: string
  placeholder?: string
  options: SelectOption[]
  onChange?: (newValue: string | null) => void
  className?: string
}

const optionsClassName =
  'fixed z-50 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'

type SelectDropdownProps = {
  open: boolean
  anchorRef: RefObject<HTMLDivElement | null>
  options: SelectOption[]
}

function SelectDropdown({ open, anchorRef, options }: SelectDropdownProps) {
  const position = useSelectDropdownPosition(open, anchorRef)

  if (!position) {
    return null
  }

  return createPortal(
    <Transition
      show={open}
      as={Fragment}
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <Listbox.Options
        className={optionsClassName}
        style={{
          top: position.top,
          bottom: position.bottom,
          left: position.left,
          width: position.width,
          maxHeight: position.maxHeight,
        }}
      >
        {options.map((option) => (
          <Listbox.Option
            key={option.value}
            className={({ active }) =>
              classNames(
                active ? 'bg-surface-hover text-brand' : 'text-heading',
                'relative cursor-default select-none py-2 pl-3 pr-9'
              )
            }
            value={option.value}
          >
            {({ selected, active }) => (
              <>
                <div className="flex">
                  <span
                    className={classNames(selected ? 'font-semibold' : 'font-normal', 'truncate')}
                  >
                    {option.label}
                  </span>
                </div>
                {selected ? (
                  <span
                    className={classNames(
                      active ? 'text-brand' : 'text-secondary',
                      'absolute inset-y-0 right-0 flex items-center pr-4'
                    )}
                  >
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Transition>,
    document.body
  )
}

export const Select = ({
  label,
  name,
  defaultValue,
  placeholder,
  options,
  onChange,
  className,
}: SelectProps) => {
  const [currentValue, setCurrentValue] = useState(defaultValue ?? null)
  const currentOption = options.find((option) => option.value === currentValue)

  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const isHydrated = useIsHydrated()

  return (
    <>
      <input ref={inputRef} type="hidden" name={name} defaultValue={currentValue ?? ''} />
      <Listbox
        value={currentValue}
        onChange={(newValue) => {
          if (inputRef.current) {
            inputRef.current.value = newValue ?? ''
          }
          setCurrentValue(newValue)
          onChange?.(newValue)
        }}
      >
        {({ open }) => (
          <>
            {label && (
              <Listbox.Label className="block text-sm font-medium leading-6 text-heading">
                {label}
              </Listbox.Label>
            )}
            <div ref={anchorRef} className={classNames('relative mt-2', className)}>
              <Listbox.Button className="relative w-full cursor-default bg-white py-1.5 pl-3 pr-10 text-left text-heading shadow-sm ring-1 ring-inset ring-border-default focus-ring-brand-inset text-sm leading-6">
                <span className="inline-flex w-full truncate">
                  {currentOption ? (
                    <span className="truncate">{currentOption?.label}</span>
                  ) : (
                    <span className="truncate">{placeholder}</span>
                  )}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-muted" aria-hidden="true" />
                </span>
              </Listbox.Button>

              {isHydrated ? (
                <SelectDropdown open={open} anchorRef={anchorRef} options={options} />
              ) : null}
            </div>
          </>
        )}
      </Listbox>
    </>
  )
}
